// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// CareRx AI prompts — migrated VERBATIM from the POT app (public/app.js + index.html) so the
// AI behaves identically. Keep these strings exact; the server SYSTEM_PROMPT (also ported
// verbatim, in CareRxAiFunctions.cs) frames every call.

// getCtx(): the de-identified clinical context POT prepends to most prompts. `data` is the
// wizard payload keyed `${stepKey}.${fieldKey}`.
export function getCtx(data: Record<string, any>): string {
  const g = (k: string) => data[k] || ''
  const drug = g('infusion_drug_order.drugName') || 'not entered'
  const indication = g('infusion_drug_order.indication') || g('diagnoses.indication') || 'not entered'
  const dx1 = g('diagnoses.dx1') || g('diagnoses.primaryDx') || 'not entered'
  const infNum = g('infusion_drug_order.infNum') || 'not entered'
  const bpS = g('vital_signs_physical_exam.bpS') || '—'
  const bpD = g('vital_signs_physical_exam.bpD') || '—'
  const hr = g('vital_signs_physical_exam.hrV') || '—'
  const o2 = g('vital_signs_physical_exam.o2V') || '—'
  return 'Clinical context (no patient identifiers):\n'
    + 'Drug: ' + drug
    + '\nIndication: ' + indication
    + '\nPrimary Dx: ' + dx1
    + '\nInfusion #: ' + infNum
    + '\nBP: ' + bpS + '/' + bpD
    + ' HR: ' + hr
    + ' O2: ' + o2 + '%'
}

// Quick-action prompts — exact strings from POT index.html sendQuick(...) buttons.
export const QUICK_PROMPTS = {
  drugMonitoring: 'What are the monitoring requirements and infusion reaction signs for the drug I entered?',
  examNote: 'Help me write the physical exam notes for this visit',
  icd10: 'What ICD-10 codes match the drug and indication I entered?',
  ddi: 'Review the full medication list I entered and flag any interactions',
}

// Per-field assist — exact prompt template from POT aiAssist(fieldId, context):
//   'Write brief clinical documentation for this POT assessment field.\nField: ' + context
//   + '\n' + getCtx() + '\nWrite a professional clinical note for this field. Be concise and specific.'
export function fieldAssistPrompt(context: string, data: Record<string, any>): string {
  return 'Write brief clinical documentation for this POT assessment field.\nField: ' + context
    + '\n' + getCtx(data)
    + '\nWrite a professional clinical note for this field. Be concise and specific.'
}

// The 11 per-field assist contexts, keyed by POT field id (the 2nd arg of aiAssist in POT).
export const FIELD_ASSIST_CONTEXT: Record<string, string> = {
  examNotes: 'physical exam findings for home infusion patient',
  medNotes: 'medication reconciliation nursing notes',
  psychoNotes: 'psychosocial assessment notes for home infusion patient',
  adlNotes: 'functional status and ADL assessment notes',
  fallNotes: 'fall risk and safety assessment notes',
  nutriNotes: 'nutritional assessment notes for home infusion patient',
  progBasis: 'clinical prognosis basis for home infusion biologic therapy patient',
  funcOutcomes: 'expected functional outcomes for biologic therapy patient',
  dischNotes: 'care planning notes for home infusion biologic therapy patient education',
  dirNotes: 'advanced directive and code status documentation for home health patient',
  freqRationale: 'visit frequency and duration rationale for home infusion biologic therapy per prescribing information',
}

// ICD-10 suggest prompt — exact from POT aiSuggest('icd10').
export function icd10Prompt(data: Record<string, any>): string {
  return 'Suggest ICD-10 codes based on this context:\n' + getCtx(data) + '\nList the 3-5 most relevant codes with descriptions.'
}

// DDI screen prompt — exact from POT aiDDICheck (meds = list of "name dose").
export function ddiPrompt(meds: string[]): string {
  return 'Check this medication list for clinically significant drug interactions for a home infusion biologic patient:\n\n'
    + meds.join('\n') + '\n\nList interactions, flag severity, state action needed. Be concise.'
}

// Documentation-gap review prompt — exact from POT aiReview.
export function reviewPrompt(data: Record<string, any>, missing: string[], step: number, total: number): string {
  return 'Review this home infusion POT assessment for documentation gaps.\n' + getCtx(data)
    + '\nEmpty required fields: ' + (missing.join(', ') || 'None')
    + '\nStep completed: ' + step + '/' + total
    + '\n\nList brief action items. Focus on documentation gaps only.'
}

// Dose verification prompt — exact from POT verifyDoseCalc (public/app.js). The caller appends
// '\n\nContext: ' + getCtx(data) exactly as POT does.
export function doseVerifyPrompt(drug: string, weightKg: number, orderedDose: string, calcDose: string): string {
  let context = 'Drug: ' + drug + '\n'
  if (weightKg > 0) context += 'Patient weight: ' + weightKg.toFixed(1) + ' kg\n'
  else context += 'Patient weight: not entered (please check Step 3 — Vitals)\n'
  context += 'Ordered dose: ' + (orderedDose || 'not entered') + '\n'
    + 'Nurse calculated dose: ' + (calcDose || 'not entered') + '\n\n'
    + 'Please verify the dose calculation using the drug\'s Prescribing Information (PI) dosage/administration section. '
    + 'Show the weight-based calculation step by step:\n'
    + '1. Confirm the standard mg/kg dosing from the PI\n'
    + '2. Calculate: weight (kg) × mg/kg = expected dose (mg)\n'
    + '3. Compare expected dose to the nurse-entered calculated dose\n'
    + '4. Flag if there is a discrepancy\n'
    + 'Reference the specific PI section for this drug.'
  return context
}

// Drug clinical-reference card prompt — exact from POT loadDrugCard (public/app.js:474-497). The reply
// is raw JSON with exactly these keys; black_box is '' when there is none.
export function drugCardPrompt(drugName: string): string {
  return 'Provide a brief clinical reference card for: ' + drugName
    + '\n\nReturn ONLY a JSON object with these exact keys:\n{"typical_dose":"","route":"","monitoring":"","reaction_signs":"","black_box":"","common_ddi":""}\n\nBe concise. black_box is empty string if none. No markdown, just raw JSON.'
}

// Goals helper prompt — exact from POT AI GOALS HELPER (returns JSON array).
export function goalsPrompt(type: string, drug: string, dx: string, timeframe: string): string {
  return 'Suggest 4 measurable ' + type + ' clinical goals for a home infusion patient.\n'
    + 'Drug: ' + drug + '\nDiagnosis: ' + dx + '\nTimeframe: ' + timeframe + '\n\n'
    + 'RESPOND ONLY WITH A JSON ARRAY. No explanations, no markdown, no backticks.\n'
    + 'Each object must have: goal_statement (string, starts with "Patient will"), target_date (string, e.g. "2 weeks" or "60 days")\n'
    + 'Example: [{"goal_statement":"Patient will...","target_date":"2 weeks"}]'
}
